import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { message } from "antd";
import i18n from "i18next";

const db = firebase.firestore();

const collectionName = "productMaterials";

/**
 * Hook to fetch and manage product materials from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {boolean} options.activeOnly - Fetch only active materials (default: false)
 * @returns {Object} - Materials data and utility functions
 */
export const useProductMaterials = (options = {}) => {
  const { autoFetch = true, realtime = true, activeOnly = false } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all product materials
   * @returns {Promise<Array>} - Array of material documents
   */
  const fetchMaterials = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let query = db.collection(collectionName);

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const snapshot = await query.get();

      if (snapshot.empty) {
        setMaterials([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setMaterials(data);
      return data;
    } catch (err) {
      console.error("Error fetching product materials:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [activeOnly]);

  /**
   * Add a new product material
   * @param {Object} materialData - Material data to add
   * @param {string} materialData.name - Material name
   * @param {string} materialData.code - Material code
   * @param {string} materialData.description - Material description (optional)
   * @returns {Promise<Object>} - Created material document
   */
  const addMaterial = useCallback(
    async (materialData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const newMaterial = {
          name: materialData.name,
          code: materialData.code,
          description: materialData.description || null,
          active: materialData.active !== undefined ? materialData.active : true,
          createdAt: dayjs().unix(),
          createdBy: auth.email,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        const docRef = await db.collection(collectionName).add(newMaterial);
        const createdMaterial = { id: docRef.id, ...newMaterial };

        message.success(i18n.t("notifications.material_added"));
        return createdMaterial;
      } catch (err) {
        console.error("Error adding product material:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Update an existing product material
   * @param {string} materialId - Material document ID
   * @param {Object} materialData - Updated material data
   * @returns {Promise<Object>} - Updated material document
   */
  const updateMaterial = useCallback(
    async (materialId, materialData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const materialRef = db.collection(collectionName).doc(materialId);
        const materialDoc = await materialRef.get();

        if (!materialDoc.exists) {
          throw new Error("Material not found");
        }

        const existingData = materialDoc.data();

        const updatedData = {
          name: materialData.name !== undefined ? materialData.name : existingData.name,
          code: materialData.code !== undefined ? materialData.code : existingData.code,
          description: materialData.description !== undefined ? materialData.description : existingData.description,
          active: materialData.active !== undefined ? materialData.active : existingData.active,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        await materialRef.update(updatedData);

        const updatedMaterial = { id: materialId, ...existingData, ...updatedData };
        message.success(i18n.t("notifications.material_updated"));
        return updatedMaterial;
      } catch (err) {
        console.error("Error updating product material:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Delete a product material
   * @param {string} materialId - Material document ID
   * @returns {Promise<void>}
   */
  const deleteMaterial = useCallback(
    async (materialId) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const materialRef = db.collection(collectionName).doc(materialId);
        const materialDoc = await materialRef.get();

        if (!materialDoc.exists) {
          throw new Error("Material not found");
        }

        await materialRef.delete();
        message.success(i18n.t("notifications.material_deleted"));
      } catch (err) {
        console.error("Error deleting product material:", err);
        setError(err);
        message.error(i18n.t("notifications.delete_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Get material by code
   * @param {string} code - Material code
   * @returns {Object|null} - Material object or null
   */
  const getMaterialByCode = useCallback(
    (code) => {
      return materials.find((material) => material.code === code) || null;
    },
    [materials],
  );

  /**
   * Get active materials only
   * @returns {Array} - Array of active materials
   */
  const getActiveMaterials = useCallback(() => {
    return materials.filter((material) => material.active === true);
  }, [materials]);

  // Set up real-time listener or fetch once
  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      setMaterials([]);
      return;
    }

    if (realtime) {
      // Real-time listener
      setIsLoading(true);
      let query = db.collection(collectionName);

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setMaterials(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error("useProductMaterials: Error in listener:", err);
          setError(err);
          setMaterials([]);
          setIsLoading(false);
        },
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      // One-time fetch
      fetchMaterials();
    }
  }, [autoFetch, realtime, activeOnly, fetchMaterials, auth.isEmpty]);

  return {
    materials,
    isLoading,
    error,
    fetchMaterials,
    addMaterial,
    updateMaterial,
    deleteMaterial,
    getMaterialByCode,
    getActiveMaterials,
  };
};
